import './styles.scss';

import moment from 'moment';
import { useContext, useEffect, useMemo } from 'react';

import { PORTFOLIO_TABS_NAME } from '../../../../../constants/reports/reports-data';
import { GlobalContext } from '../../../../../providers/global-provider';
import {
  DATA_DISPLAY_KEYS,
  PortfolioManagementContext,
} from '../../../../../providers/portfolio-management';
import { PositionsFiltersContext } from '../../../../../providers/positions/positions-filters-provider';
import { PositionsContext } from '../../../../../providers/positions/positions-provider';
import { IAccountOption } from '../../../../../providers/venue-provider';
import Utils from '../../../../../utils/immersive_utils';

import OrdersTable from './orders-table';

type OrdersType = {
  accountOption: IAccountOption;
};

type OrderItemType = {
  AccountID: string;
  AccountName: string;
  direction: string;
  order_id: string;
  order_type: string;
  order_state: string;
  price: string;
  amount: string;
  average_price: string;
  creation_timestamp: string;
  filled_amount: string;
  profit_loss: string;
  reduce_only: string;
  instrument_name: string;
  timestamp: string;
  Underlying: string;
  Exchange: string;
  id: string;
  label: string;
  contract: string;
  values: any[];
};

const formatterCurrency = (val: string, params?: any) => {
  const paramIsANumber = params && typeof params === 'number';
  const decimals = paramIsANumber ? params : 0;

  return val && !!val.length ? Utils.valueToCurrency(parseFloat(val), decimals) : '-';
};

const values = [
  {
    value: 'direction',
    type: 'string',
    formatter: (val: any) => {
      const GREEN = ['long', 'close buy', 'buy', 'open buy'];
      const RED = ['short', 'open sell', 'sell', 'close sell'];

      if (GREEN.includes(val)) {
        return <span className="formatter-green">{val}</span>;
      }

      if (RED.includes(val)) {
        return <span className="formatter-red">{val}</span>;
      }

      return <span>{val}</span>;
    },
  },
  { value: 'amount', type: 'number', sum: true, formatter: formatterCurrency },
  {
    value: 'price',
    type: 'number',
    formatter: (value: string, element: any) => {
      if (element.contract === 'Futures') {
        return formatterCurrency(value);
      } else {
        return parseFloat(value).toFixed(4);
      }
    },
  },
  { value: 'trigger', type: 'string' },
  { value: 'trigger_price', type: 'number', formatter: formatterCurrency },
  { value: 'trigger_offset', type: 'number' },
  { value: 'Initial Margin', type: 'number' },
  { value: 'order_id', type: 'string' },
  { value: 'order_type', type: 'string' },
  {
    value: 'time_in_force',
    type: 'string',
    formatter: (val: any) => {
      return <div className={'time-in-force'}>{val}</div>;
    },
  },
  {
    value: 'creation_timestamp',
    type: 'string',
    formatter: (val: any) => {
      return <span>{moment(parseInt(val)).utc().format('YYYY-MM-DD')}</span>;
    },
  },
];

const HEADER = [
  'Side',
  'Amount',
  'Price',
  'Trigger',
  'Trigger Price',
  'Trigger Offset',
  'Initial Margin',
  'Order ID',
  'Type',
  'TIF',
  'Date',
];

function groupByHierarchy(data: any[], token: string) {
  const groupedData: Record<string, any> = {};

  const sumArrays = (arrays: number[][]) =>
    arrays.reduce((acc, arr) => {
      arr.forEach((value, index) => {
        acc[index] = (acc[index] || 0) + value;
      });
      return acc;
    }, []);

  const processValues = (values: any[]) => values.map((value) => parseFloat(value) || 0);

  data.forEach((item) => {
    const { Exchange, Underlying, contract } = item;

    const date = moment(parseInt(item['creation_timestamp'])).utc().format('YYYY-MM-DD');

    const isTokensAllowed = token.includes(Underlying);

    if (isTokensAllowed) {
      if (!groupedData[Exchange]) {
        groupedData[Exchange] = {
          id: Exchange,
          label: Exchange,
          children: [],
          values: processValues(item.values).map((v, i) => (values[i]?.sum ? v : '')),
        };
      } else {
        let sumArr: any = sumArrays([groupedData[Exchange].values, processValues(item.values)]);

        sumArr = sumArr.map((_value: any, index: number) => {
          return <div key={index}>&nbsp;</div>;
        });

        groupedData[Exchange].values = sumArr;
      }

      const exchangeNode = groupedData[Exchange];

      let underlyingNode = exchangeNode.children.find(
        (child: any) => child.id === `${Exchange}_${Underlying}`,
      );
      if (!underlyingNode) {
        underlyingNode = {
          id: `${Exchange}_${Underlying}`,
          label: Underlying,
          children: [],
          values: processValues(item.values).map((v, i) => (values[i]?.sum ? v : '')),
        };

        exchangeNode.children.push(underlyingNode);
      } else {
        const sumArr = sumArrays([underlyingNode.values, processValues(item.values)]);

        const sumArrRender = sumArr.map((_value: any, index: number) => {
          return <div key={index}>&nbsp;</div>;
        });

        underlyingNode.values = sumArrRender;
      }

      let dateNode = underlyingNode.children.find(
        (child: any) => child.id === `${Exchange}_${Underlying}_${date}`,
      );

      if (!dateNode) {
        dateNode = {
          id: `${Exchange}_${Underlying}_${date}`,
          label: date,
          children: [],
          values: processValues(item.values).map((v, i) => (values[i]?.sum ? v : '')),
          timestamp: item.timestamp,
        };
        underlyingNode.children.push(dateNode);
      } else {
        const sumArr = sumArrays([dateNode.values, processValues(item.values)]);

        const sumArrRender = sumArr.map((_value: any, index: number) => {
          return <div key={index}>&nbsp;</div>;
        });

        dateNode.values = sumArrRender;
      }

      let contractNode = dateNode.children.find(
        (child: any) => child.id === `${Exchange}_${Underlying}_${date}_${contract}`,
      );

      if (!contractNode) {
        contractNode = {
          id: `${Exchange}_${Underlying}_${date}_${contract}`,
          label: contract,
          children: [],
          values: processValues(item.values).map((v, i) => (values[i]?.sum ? v : '')),
        };
        dateNode.children.push(contractNode);
      } else {
        let sumArr: any = sumArrays([contractNode.values, processValues(item.values)]);

        sumArr = sumArr.map((value: any, index: number) => {
          if (values[index]?.sum) {
            return value;
          } else {
            return '';
          }
        });

        contractNode.values = sumArr;
      }

      contractNode.children.push(item);
    }
  });

  const newData = Object.values(groupedData);
  return newData;
}

const mappingToDeribitFormat = (okxData: any) => {
  return {
    AccountID: okxData.AccountID,
    AccountName: okxData.AccountName,
    direction: okxData.side,
    order_id: okxData.ordId,
    order_type: okxData.ordType,
    order_state: okxData.state,
    price: okxData.px,
    amount: okxData.sz,
    average_price: okxData.avgPx,
    creation_timestamp: okxData.cTime,
    filled_amount: okxData.fillSz,
    profit_loss: okxData.pnl,
    reduce_only: okxData.reduceOnly,
    instrument_name: okxData.instId,
  };
};

const mappingBybitToDeribitFormat = (bybitData: any) => {
  return {
    AccountID: bybitData.AccountID,
    AccountName: bybitData.AccountName,
    direction: bybitData.side,
    order_id: bybitData.order_id,
    order_type: bybitData.type,
    order_state: bybitData.state || 'open',
    price: bybitData.price,
    amount: bybitData.amount,
    time_in_force: bybitData.tif,
    creation_timestamp: bybitData.date,
    instrument_name: bybitData.instrument_name,
  };
};

const mappingBullishToDeribitFormat = (bullishData: any) => {
  return {
    AccountID: bullishData.AccountID,
    AccountName: bullishData.AccountName,
    direction: bullishData.side,
    order_id: bullishData.order_id,
    order_type: bullishData.type,
    order_state: bullishData.state || 'open',
    price: bullishData.price,
    amount: bullishData.amount,
    time_in_force: bullishData.tif,
    creation_timestamp: bullishData.date,
    instrument_name: bullishData.instrument_name,
  };
};

const mappingCoincallToDeribitFormat = (coincallData: any) => {
  return {
    AccountID: coincallData.AccountID,
    AccountName: coincallData.AccountName,
    direction: coincallData.side,
    order_id: coincallData.order_id,
    order_type: coincallData.type,
    order_state: coincallData.state || 'open',
    price: coincallData.price,
    amount: coincallData.amount,
    time_in_force: coincallData.tif,
    creation_timestamp: coincallData.date,
    instrument_name: coincallData.instrument_name,
  };
};

const Orders = ({ accountOption }: OrdersType) => {
  const { portfolioCurrentTab } = useContext(GlobalContext);
  const { orders: data, setOrders: setData, isLoadingOrders } = useContext(PositionsContext);
  const { getDataFromAccount, getAggregatedData, selectedDataDisplayOption } = useContext(
    PortfolioManagementContext,
  );
  const {
    filters: { setErrorOrders, token },
  } = useContext(PositionsFiltersContext);

  const isDataDisplayPerAccount = selectedDataDisplayOption === DATA_DISPLAY_KEYS.perAccount;

  const groupedData = useMemo(() => {
    let newData: any[] = [];

    const processAndSanitizeData = (dataArray: any[], exchange: string) => {
      const sanitalizedData: OrderItemType[] = [];

      dataArray.forEach((elementRaw: any, index: number) => {
        let element = elementRaw;
        if (exchange === 'okx') {
          element = mappingToDeribitFormat(elementRaw);
        } else if (exchange === 'bybit') {
          element = mappingBybitToDeribitFormat(elementRaw);
        } else if (exchange === 'bullish') {
          element = mappingBullishToDeribitFormat(elementRaw);
        } else if (exchange === 'coincall') {
          element = mappingCoincallToDeribitFormat(elementRaw);
        }

        const checkIfIsFuture = () => {
          const regex = /^\d{4}-\d{2}-\d{2}$/;
          return (
            !!element['instrument_name'] &&
            (regex?.test(element['instrument_name']) ||
              element['instrument_name']?.includes('PERPETUAL'))
          );
        };

        const getContract = () => {
          return checkIfIsFuture() ? 'Futures' : 'Options';
        };

        const contract = getContract();

        sanitalizedData[index] = {
          ...element,
          Underlying: element['instrument_name']?.split('-')[0],
          Exchange: Utils.capitalizeFirstLetter(exchange),
          id: `${element['ID']}_${Math.floor(Math.random() * 90000) + 10000}`,
          label: element['instrument_name'],
          contract: contract,
          values: values.map((val: any) => {
            if (!!val.formatter && !val?.sum) {
              return val.formatter(element[val.value], element);
            }

            if (val.type === 'number') {
              return Utils.formatNumber(element[val.value] || '0', 2);
            } else {
              return element[val.value] || '-';
            }
          }),
        };
      });

      sanitalizedData.sort((a: any, b: any) => {
        return parseInt(b.creation_timestamp) - parseInt(a.creation_timestamp);
      });

      return groupByHierarchy(sanitalizedData, token);
    };

    if (data) {
      if (Array.isArray(data)) {
        // Per account scenario
        const exchange = accountOption.exchange;
        newData = [processAndSanitizeData(data, exchange)];
      } else {
        // Aggregated scenario
        const newDataArray = [];

        if (data.deribit) {
          newDataArray.push(processAndSanitizeData(data.deribit, 'deribit'));
        }

        if (data.okx) {
          newDataArray.push(processAndSanitizeData(data.okx, 'okx'));
        }

        newData = newDataArray;
      }
    }

    return newData;
  }, [data, token, values, accountOption]);

  useEffect(() => {
    if (
      isLoadingOrders ||
      !isDataDisplayPerAccount ||
      portfolioCurrentTab !== PORTFOLIO_TABS_NAME.positionsAndOrders
    )
      return;

    const dataAccount = getDataFromAccount('ORDERS', accountOption);

    if (!dataAccount) {
      setErrorOrders(false);
      setData(null);
      return;
    }

    if (dataAccount.error) {
      setErrorOrders(true);
      setData(null);
      return;
    }

    if (dataAccount.data) {
      setData(dataAccount.data);
      setErrorOrders(false);
      return;
    }

    setData(null);
    setErrorOrders(false);
  }, [portfolioCurrentTab, accountOption, isLoadingOrders, isDataDisplayPerAccount]);

  useEffect(() => {
    if (
      isLoadingOrders ||
      isDataDisplayPerAccount ||
      portfolioCurrentTab !== PORTFOLIO_TABS_NAME.positionsAndOrders
    )
      return;

    const dataAggregatedAccounts = getAggregatedData('ORDERS');

    if (!dataAggregatedAccounts) {
      setData(null);
      setErrorOrders(false);
      return;
    }

    if (dataAggregatedAccounts.error.deribit || dataAggregatedAccounts.error.okx) {
      setData(null);
      setErrorOrders(true);
      return;
    }

    if (dataAggregatedAccounts.data) {
      setData(dataAggregatedAccounts.data);
      setErrorOrders(false);
      return;
    }

    setData(null);
    setErrorOrders(false);
  }, [portfolioCurrentTab, accountOption, isLoadingOrders, isDataDisplayPerAccount]);

  let hasAllData = false;

  if (isDataDisplayPerAccount) {
    hasAllData = data && data.length > 0;
  } else {
    hasAllData =
      (data?.deribit && data?.deribit?.length > 0) || (data?.okx && data?.okx?.length > 0);
  }

  const hasOnFilterData = groupedData[0]?.length > 0;

  return (
    <div className="orders-container">
      <span className="table-label">Orders</span>
      <OrdersTable
        data={groupedData}
        header={HEADER}
        valuesFormat={values}
        hasAllData={hasAllData}
        hasOnFilterData={hasOnFilterData}
      />
    </div>
  );
};

export default Orders;
